import React from 'react';
import { TitleBlockTemplate, TitleBlockCell, TitleBlockData } from '@/app/types/titleblock';
import { PlanSheet } from '@/app/types/plan';

interface DynamicTitleBlockProps {
  template: TitleBlockTemplate;
  data: TitleBlockData; // Project values
  plan?: PlanSheet; // Optional reference to plan for system variables (scale, date)
  scale?: number; // Display scale factor (default 1.0)
  showPlaceholders?: boolean; // If true, shows {{key}} or [key] for missing values. If false, renders empty.
  selectedCellIds?: string[];
  onCellClick?: (cellId: string, e: React.MouseEvent) => void;
  onCellDoubleClick?: (cellId: string, e: React.MouseEvent) => void;
  onCellMouseDown?: (cellId: string, e: React.MouseEvent) => void;
  onCellMouseEnter?: (cellId: string, e: React.MouseEvent) => void;
  onCellMouseUp?: (cellId: string, e: React.MouseEvent) => void;
  onCellChange?: (cellId: string, value: string, type: 'text' | 'attribute') => void;
  onDataChange?: (newData: Partial<TitleBlockData>) => void;
  onLayoutChange?: (newCols: number[], newRows: number[]) => void;
  uiZoom?: number;
}

export function DynamicTitleBlock({ 
  template, 
  data, 
  plan,
  scale = 1.0,
  showPlaceholders = false,
  selectedCellIds = [],
  onCellClick,
  onCellDoubleClick,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  onCellChange,
  onDataChange,
  onLayoutChange,
  uiZoom = 1.0
}: DynamicTitleBlockProps) {
  
  // Internal state for editing
  const [editingCellId, setEditingCellId] = React.useState<string | null>(null);
  const [resizing, setResizing] = React.useState<{ 
      type: 'col' | 'row', 
      index: number, 
      startScreenPos: number, 
      startSize: number 
  } | null>(null);

  // Constants
  const PIXELS_PER_MM = 3.7795275591;

  // Global Drag Handlers
  React.useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
        const deltaScreen = (resizing.type === 'col' ? e.clientX : e.clientY) - resizing.startScreenPos;
        const deltaMM = deltaScreen / (uiZoom * PIXELS_PER_MM);
        
        const newSize = Math.max(1, resizing.startSize + deltaMM); // Min 1mm
        
        if (resizing.type === 'col') {
            const newCols = [...template.columns];
            newCols[resizing.index] = newSize;
            onLayoutChange?.(newCols, template.rows);
        } else {
            const newRows = [...template.rows];
            newRows[resizing.index] = newSize;
            onLayoutChange?.(template.columns, newRows);
        }
    };

    const handleMouseUp = () => {
        setResizing(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, template, uiZoom, onLayoutChange]);
  
  // When showPlaceholders (edit mode) changes to false, clear editing
  React.useEffect(() => {
    if (!showPlaceholders) {
      setEditingCellId(null);
    }
  }, [showPlaceholders]);

  // 1. Pre-calculate Grid Coordinates
  // xCoords[i] = x position of column i
  // yCoords[j] = y position of row j
  const colWidths = template.columns;
  const rowHeights = template.rows;
  
  const xCoords: number[] = [0];
  let currentX = 0;
  colWidths.forEach(w => {
    currentX += w;
    xCoords.push(currentX);
  });
  
  const yCoords: number[] = [0];
  let currentY = 0;
  rowHeights.forEach(h => {
    currentY += h;
    yCoords.push(currentY);
  });
  
  const totalWidth = currentX;
  const totalHeight = currentY;

  // Helper to resolve cell content
  const resolveContent = (cell: TitleBlockCell): string => {
    if (cell.type === 'text' || cell.type === 'image') return cell.value;
    if (cell.type === 'attribute') {
      // Look up variable
      const key = cell.value.replace(/[{}]/g, ''); // strip {{ }} if present
      
      // Check data prop first
      if (data && data[key] !== undefined) return data[key];
      
      // Check common system variables if not found
      if (key === 'date') return new Date().toLocaleDateString();
      if (key === 'scale' && plan) return `1:${(1 / plan.viewport.scale).toFixed(0)}`;
      if (key === 'plan_number' && plan) return plan.id.split('-').pop() || '01';
      
      // If we are in "Show Placeholders" mode (Edit Mode), show the key.
      // Otherwise (View Mode), show nothing to keep it clean.
      return showPlaceholders ? `{{${key}}}` : '';
    }
    return '';
  };

  // Render Cell
  const renderCell = (cell: TitleBlockCell) => {
    const cIdx = cell.colIndex;
    const rIdx = cell.rowIndex;
    const cSpan = cell.colSpan || 1;
    const rSpan = cell.rowSpan || 1;
    
    const x = xCoords[cIdx];
    const y = yCoords[rIdx];
    const w = xCoords[cIdx + cSpan] - x;
    const h = yCoords[rIdx + rSpan] - y;
    
    const styles = { ...template.defaultStyles, ...cell.styles };
    
    // Resolve borders
    const strokeColor = styles.color || '#000000';
    const borderDefaultWidth = 0.25;
    
    const bTop = styles.borderTop?.show ?? false;
    const bRight = styles.borderRight?.show ?? false;
    const bBottom = styles.borderBottom?.show ?? false;
    const bLeft = styles.borderLeft?.show ?? false;
    
    const content = resolveContent(cell);
    const isSelected = selectedCellIds.includes(cell.id);
    const isEditing = editingCellId === cell.id;
    const isEditable = showPlaceholders && (cell.type === 'attribute' || cell.type === 'text');

    // Key for attribute update
    const attributeKey = cell.type === 'attribute' ? cell.value.replace(/[{}]/g, '') : '';
    // For text, we use cell.value directly. For attributes, we use data[key].
    const rawValue = cell.type === 'attribute' 
        ? (data && attributeKey && data[attributeKey] !== undefined ? data[attributeKey] : '')
        : cell.value;

    return (
      <g 
        key={cell.id} 
        transform={`translate(${x}, ${y})`}
        onClick={(e) => {
           // Single Click: Select only
           if (onCellClick) {
              e.stopPropagation();
              onCellClick(cell.id, e);
           }
        }}
        onDoubleClick={(e) => {
           // Double Click: Enter Edit Mode if editable
           if (showPlaceholders && isEditable) {
              e.stopPropagation();
              setEditingCellId(cell.id);
           } else if (onCellDoubleClick) {
              e.stopPropagation();
              onCellDoubleClick(cell.id, e);
           }
        }}
        onMouseDown={(e) => {
          if (onCellMouseDown) {
            e.stopPropagation();
            onCellMouseDown(cell.id, e);
          }
        }}
        onMouseEnter={(e) => {
          if (onCellMouseEnter) {
            // e.stopPropagation(); // Don't stop propagation, we might need it for parent hover effects? actually safe to stop here
            onCellMouseEnter(cell.id, e);
          }
        }}
        onMouseUp={(e) => {
          if (onCellMouseUp) {
            onCellMouseUp(cell.id, e);
          }
        }}
        style={{ cursor: isEditable ? 'cell' : onCellClick ? 'pointer' : 'default' }}
      >
        {/* Background / Hit Area */}
        <rect 
          x="0" 
          y="0" 
          width={w} 
          height={h} 
          fill={styles.backgroundColor || (isSelected ? 'rgba(59, 130, 246, 0.1)' : 'transparent')}
          stroke={isSelected || isEditing ? '#3b82f6' : 'none'}
          strokeWidth={isSelected || isEditing ? 0.5 : 0}
        />
        
        {/* Borders */}
        {/* We draw lines instead of rect stroke to handle individual borders */}
        {bTop && (
          <line x1="0" y1="0" x2={w} y2="0" stroke={styles.borderTop?.color || strokeColor} strokeWidth={styles.borderTop?.width || borderDefaultWidth} />
        )}
        {bRight && (
          <line x1={w} y1="0" x2={w} y2={h} stroke={styles.borderRight?.color || strokeColor} strokeWidth={styles.borderRight?.width || borderDefaultWidth} />
        )}
        {bBottom && (
          <line x1="0" y1={h} x2={w} y2={h} stroke={styles.borderBottom?.color || strokeColor} strokeWidth={styles.borderBottom?.width || borderDefaultWidth} />
        )}
        {bLeft && (
          <line x1="0" y1="0" x2="0" y2={h} stroke={styles.borderLeft?.color || strokeColor} strokeWidth={styles.borderLeft?.width || borderDefaultWidth} />
        )}

        {/* Content */}
        {isEditing ? (
           <foreignObject x={0} y={0} width={w} height={h}>
             {/* Use a div to reset styling context inside SVG */}
             <div 
                xmlns="http://www.w3.org/1999/xhtml" 
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: styles.verticalAlign === 'top' ? 'flex-start' : styles.verticalAlign === 'bottom' ? 'flex-end' : 'center',
                  justifyContent: styles.textAlign === 'center' ? 'center' : styles.textAlign === 'right' ? 'flex-end' : 'flex-start',
                  paddingLeft: styles.paddingLeft || styles.padding || 1,
                  paddingRight: styles.paddingRight || styles.padding || 1,
                  paddingTop: styles.paddingTop || styles.padding || 0,
                  paddingBottom: styles.paddingBottom || styles.padding || 0,
                  boxSizing: 'border-box'
                }}
             >
                <input
                  autoFocus
                  defaultValue={rawValue}
                  onBlur={(e) => {
                    const newVal = e.target.value;
                    const isAttr = newVal.startsWith('{{') || cell.type === 'attribute'; // Keep as attribute if it was one, or if typing {{
                    
                    if (cell.type === 'attribute' && attributeKey && onDataChange) {
                       // Update Data Value
                       onDataChange({ [attributeKey]: newVal });
                    } else if (cell.type === 'text' && onCellChange) {
                       // Update Template Text
                       onCellChange(cell.id, newVal, 'text');
                    }
                    setEditingCellId(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                       e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                       setEditingCellId(null);
                       e.stopPropagation(); // Don't close parent mode
                    }
                  }}
                  style={{
                    width: '100%',
                    background: 'white',
                    border: 'none',
                    outline: '1px solid #3b82f6',
                    fontFamily: styles.fontFamily || 'Arial, sans-serif',
                    fontSize: `${styles.fontSize || 3}px`, // Map SVG unit to px
                    fontWeight: styles.fontWeight || 'normal',
                    color: styles.color || 'black',
                    padding: 0,
                    margin: 0,
                    lineHeight: 1
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
             </div>
           </foreignObject>
        ) : cell.type === 'image' ? (
           // Image rendering
           <image 
             href={content} 
             x={styles.paddingLeft || styles.padding || 0} 
             y={styles.paddingTop || styles.padding || 0} 
             width={w - ((styles.paddingLeft || styles.padding || 0) + (styles.paddingRight || styles.padding || 0))} 
             height={h - ((styles.paddingTop || styles.padding || 0) + (styles.paddingBottom || styles.padding || 0))} 
             preserveAspectRatio="xMidYMid meet"
           />
        ) : (
           // Text rendering
           <text
             x={
               styles.textAlign === 'center' ? w / 2 :
               styles.textAlign === 'right' ? w - (styles.paddingRight || styles.padding || 1) :
               (styles.paddingLeft || styles.padding || 1)
             }
             y={
               styles.verticalAlign === 'top' ? ((styles.fontSize || 3) + (styles.paddingTop || styles.padding || 0)) : 
               styles.verticalAlign === 'bottom' ? h - (styles.paddingBottom || styles.padding || 1) :
               (h / 2) + ((styles.fontSize || 3) / 3) // Approx vertical center adjustment
             }
             textAnchor={
               styles.textAlign === 'center' ? 'middle' :
               styles.textAlign === 'right' ? 'end' : 'start'
             }
             fontFamily={styles.fontFamily || 'Arial, sans-serif'}
             fontSize={styles.fontSize || 3}
             fontWeight={styles.fontWeight || 'normal'}
             fill={styles.color || 'black'}
             style={{ whiteSpace: 'pre' }}
           >
             {content}
           </text>
        )}
      </g>
    );
  };

  return (
    <g>
      {/* Background Mask - Ensures Title Block is opaque and hides underlying drawing */}
      <rect 
        x="0" 
        y="0" 
        width={totalWidth} 
        height={totalHeight} 
        fill="white" 
      />

      {/* Optional: Render Grid for Debugging or Empty State */}
      {/* 
      <rect x="0" y="0" width={totalWidth} height={totalHeight} fill="none" stroke="magenta" strokeWidth="0.1" strokeDasharray="1,1" />
      */}
      
      {template.cells.map(renderCell)}

      {/* Resize Handles (Only in Edit Mode) - RENDER LAST TO BE ON TOP */}
      {showPlaceholders && onLayoutChange && (
        <g>
            {/* Column Resizers - Vertical Lines at each column boundary (except 0) */}
            {xCoords.map((x, i) => {
                if (i === 0) return null; // Don't resize left edge
                // Handle at x controls column i-1 width
                const colIndex = i - 1; 
                if (colIndex >= template.columns.length) return null;

                return (
                    <line 
                        key={`cw-${i}`}
                        x1={x} y1={-2} x2={x} y2={totalHeight + 2}
                        stroke="rgba(0,0,0,0)" // Transparent but hit-testable
                        strokeWidth={3} // Wider Hit area
                        style={{ cursor: 'col-resize', pointerEvents: 'all' }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            setResizing({
                                type: 'col',
                                index: colIndex,
                                startScreenPos: e.clientX,
                                startSize: template.columns[colIndex]
                            });
                        }}
                    />
                );
            })}
            
            {/* Visual Guide for Columns (Thin dashed line) */}
            {xCoords.map((x, i) => i > 0 && i <= template.columns.length && (
                 <line key={`cg-${i}`} x1={x} y1={0} x2={x} y2={totalHeight} stroke="#3b82f6" strokeWidth={0.3} strokeDasharray="2,1" opacity={0.8} pointerEvents="none"/>
            ))}

            {/* Row Resizers - Horizontal Lines */}
            {yCoords.map((y, i) => {
                if (i === 0) return null;
                const rowIndex = i - 1;
                if (rowIndex >= template.rows.length) return null;

                return (
                    <line 
                        key={`rh-${i}`}
                        x1={-2} y1={y} x2={totalWidth + 2} y2={y}
                        stroke="rgba(0,0,0,0)"
                        strokeWidth={3}
                        style={{ cursor: 'row-resize', pointerEvents: 'all' }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            setResizing({
                                type: 'row',
                                index: rowIndex,
                                startScreenPos: e.clientY,
                                startSize: template.rows[rowIndex]
                            });
                        }}
                    />
                );
            })}
            
             {/* Visual Guide for Rows */}
             {yCoords.map((y, i) => i > 0 && i <= template.rows.length && (
                 <line key={`rg-${i}`} x1={0} y1={y} x2={totalWidth} y2={y} stroke="#3b82f6" strokeWidth={0.3} strokeDasharray="2,1" opacity={0.8} pointerEvents="none"/>
            ))}
        </g>
      )}
    </g>
  );
}
